import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { authService, LoginRequest } from '@/services/authService';
import { analytics } from '@/services/analytics';
import { isSocialAuthCancelled } from '@/native/socialAuth';

interface LoginPageProps {
  entrySource?: string;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToForgotPassword?: () => void;
  onLoginSuccess?: () => void;
}

const LastUsedBadge: React.FC<{ inline?: boolean }> = ({ inline = false }) => (
  <span className={`pointer-events-none rounded-full border border-blue-400 bg-blue-50 px-2 py-1 text-[11px] font-semibold leading-none text-blue-600 shadow-sm ${inline ? '' : 'absolute -top-2 right-4'}`}>
    Last used
  </span>
);

const LoginPage: React.FC<LoginPageProps> = ({ entrySource, onNavigateToSignup, onNavigateToForgotPassword, onLoginSuccess }) => {
  const { theme } = useTheme();
  const { login, isLoading: authLoading, refreshUser } = useAuth();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastLoginMethod] = useState(() => authService.getLastLoginMethod());

  useEffect(() => {
    analytics.track('Login Modal', {
      source: entrySource || 'unknown'
    });
  }, [entrySource]);

  // Email login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await login('google');
    } catch (error) {
      if (!isSocialAuthCancelled(error)) {
        console.error('Google login failed:', error);
        setError(error instanceof Error ? error.message : 'Google login failed');
      }
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await login('apple');
    } catch (error) {
      if (!isSocialAuthCancelled(error)) {
        console.error('Apple login failed:', error);
        setError(error instanceof Error ? error.message : 'Apple login failed');
      }
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (error) setError('');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate form
      if (!loginData.email.trim()) {
        setError('Please enter your email address');
        setIsLoading(false);
        return;
      }
      if (!loginData.password) {
        setError('Please enter your password');
        setIsLoading(false);
        return;
      }

      // Login with email/password
      const loginRequest: LoginRequest = {
        email: loginData.email.trim(),
        password: loginData.password
      };

      await authService.loginWithPassword(loginRequest);
      authService.setLastLoginMethod('email');

      // Refresh user context
      await refreshUser();

      // Navigate to home page (consistent with OAuth flow)
      if (onLoginSuccess) {
        onLoginSuccess();
      }

    } catch (error) {
      console.error('Email login error:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300 rounded-lg`}>
      <div className="w-full max-w-md mx-auto">
        {/* Page Header */}
        <div className="mb-10 mt-8 text-center">
          <h1 className="text-2xl 2xs:text-3xl font-regular mb-2">Sign in to Aleph</h1>
        </div>

        {/* Login Form */}
        <div className="w-full">
          <div className={`${isDarkMode ? '' : ''}`}>
            {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* OAuth Buttons */}
              <div className="space-y-4 mb-6">
                {/* Google Login Button */}
                <button
                  onClick={handleGoogleLogin}
                  disabled={isLoading || authLoading}
                  className={`relative w-full flex items-center justify-center px-4 py-3 border border-blue-600 rounded-full shadow-sm text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                      : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                  } ${(isLoading || authLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}`}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="none">
                    <path
                      fill="white"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="white"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="white"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="white"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {(isLoading || authLoading) ? 'Signing in...' : 'Continue with Google'}
                  {lastLoginMethod === 'google' && <LastUsedBadge />}
                </button>

                <button
                  onClick={handleAppleLogin}
                  disabled={isLoading || authLoading}
                  className={`relative w-full flex items-center justify-center px-4 py-3 border border-blue-600 rounded-full shadow-sm text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                      : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                  } ${(isLoading || authLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}`}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.05 12.54c-.03-3.04 2.48-4.52 2.59-4.59-1.43-2.09-3.65-2.38-4.44-2.4-1.87-.2-3.69 1.12-4.64 1.12-.97 0-2.43-1.1-4.01-1.07-2.03.03-3.93 1.21-4.97 3.04-2.15 3.72-.55 9.18 1.51 12.19 1.03 1.47 2.23 3.1 3.81 3.04 1.55-.06 2.13-.98 4-.98 1.85 0 2.4.98 4.02.94 1.66-.03 2.71-1.47 3.7-2.95 1.19-1.69 1.67-3.36 1.69-3.45-.04-.01-3.23-1.23-3.26-4.89ZM13.98 3.57A5.39 5.39 0 0 0 15.21 0a5.49 5.49 0 0 0-3.55 1.69 5.13 5.13 0 0 0-1.26 3.42 4.53 4.53 0 0 0 3.58-1.54Z"/>
                  </svg>
                  {(isLoading || authLoading) ? 'Signing in...' : 'Continue with Apple'}
                  {lastLoginMethod === 'apple' && <LastUsedBadge />}
                </button>
              </div>

              {/* Divider */}
              <div className="relative mb-6 mt-8">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-2 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Email Form */}
              <form className="space-y-4" onSubmit={handleEmailLogin}>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="email" className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Address
                    </label>
                    {lastLoginMethod === 'email' && <LastUsedBadge inline />}
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={loginData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 rounded-full border ${
                      isDarkMode
                        ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                        : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    value={loginData.password}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 rounded-full border ${
                      isDarkMode
                        ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                        : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your password"
                  />
                </div>

                <div className="flex flex-col 2xs:flex-row 2xs:items-center 2xs:justify-between gap-3">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className={`h-4 w-4 rounded border-gray-300 ${
                        isDarkMode ? 'text-blue-600 focus:ring-blue-500' : 'text-blue-600 focus:ring-blue-500'
                      }`}
                    />
                    <label htmlFor="remember-me" className={`ml-2 block text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={onNavigateToForgotPassword}
                      className={`font-medium hover:underline cursor-pointer ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                    >
                      Forgot your password?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || authLoading}
                  className={`relative w-full flex justify-center py-3 px-4 border rounded-full shadow-sm text-sm font-medium text-white transition-colors ${
                    isLoading || authLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : `${
                          isDarkMode
                            ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                            : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                        } cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`
                  }`}
                >
                  {(isLoading || authLoading) ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              {/* Sign Up Link */}
              <div className="mt-6 text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Don't have an account?{' '}
                  <button
                    onClick={() => onNavigateToSignup?.('login_modal')}
                    className={`font-medium hover:underline cursor-pointer ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                  >
                    Sign up
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default LoginPage;
